import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  SimpleGrid,
  ThemeIcon,
  Table,
  Badge,
  Loader,
  Center,
  ScrollArea,
  Card,
  Alert,
  Button,
  Timeline
} from "@mantine/core";
import {
  IconPackage,
  IconBellRinging,
  IconCalendarEvent,
  IconRefresh,
  IconAlertCircle,
  IconTruckDelivery,
  IconCheck
} from "@tabler/icons-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

export default function SekolahDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/sekolah/dashboard");
      setData(res.data);
    } catch (err) {
      console.error("Gagal memuat dashboard:", err);
      setError("Gagal mengambil data dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) return <Center h="100vh"><Loader size="xl" /></Center>;

  if (error || !data) {
    return (
      <Center h="80vh" style={{ flexDirection: 'column' }}>
         <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="md">
            {error || "Data tidak ditemukan."}
         </Alert>
         <Button variant="subtle" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>
            Coba Refresh
         </Button>
      </Center>
    );
  }

  // Safe Destructuring
  const { 
    sekolah_nama = "Sekolah",
    stats = { total_paket_diterima: 0, perlu_konfirmasi: 0, jadwal_next: null }, 
    chart = [], 
    recent = [] 
  } = data;

  // --- KOMPONEN STATS CARD ---
  const StatCard = ({ title, value, icon, color, desc, action }) => (
    <Paper withBorder p="md" radius="md" style={{ position: 'relative', overflow: 'hidden' }}>
      <Group justify="space-between" mb="xs">
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">{title}</Text>
        <ThemeIcon variant="light" color={color} size="lg" radius="md">{icon}</ThemeIcon>
      </Group>
      
      <Group align="flex-end" gap="xs">
        <Text fw={700} size="xl" lh={1}>{value}</Text>
        {desc && <Text size="xs" c="dimmed" mb={2}>{desc}</Text>}
      </Group>

      {action && (
          <Button 
            fullWidth 
            variant="light" 
            color={color} 
            mt="md" 
            size="xs" 
            onClick={action}
          >
            Lihat Detail
          </Button>
      )}
    </Paper>
  );

  // --- ROWS TABEL ---
  const rows = recent.map((item) => (
    <Table.Tr key={item.id}>
      <Table.Td>
        <Text fz="sm" fw={500}>
            {new Date(item.tanggal).toLocaleDateString('id-ID', {day: 'numeric', month: 'short'})}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="sm">{item.menu}</Text>
        <Text fz="xs" c="dimmed">{item.penyedia}</Text>
      </Table.Td>
      <Table.Td>
         <Badge variant="outline" color="gray">{item.jumlah} Porsi</Badge>
      </Table.Td>
      <Table.Td>
         <Badge 
            color={item.status_terima === 'Selesai' ? 'green' : (item.status_terima === 'Perlu Konfirmasi' ? 'orange' : 'blue')} 
            variant="light"
         >
            {item.status_terima}
         </Badge>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div style={{ padding: 20 }}>
      <Group justify="space-between" mb="lg">
         <div>
            <Title order={2}>Halo, {sekolah_nama}!</Title>
            <Text c="dimmed">Selamat datang di panel penerimaan GiziMon</Text>
         </div>
         <Button variant="white" onClick={fetchDashboard} leftSection={<IconRefresh size={16}/>}>Refresh</Button>
      </Group>

      {/* 1. STATISTIK UTAMA */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        {/* Card Alert: Perlu Konfirmasi */}
        <StatCard 
            title="Perlu Konfirmasi" 
            value={stats.perlu_konfirmasi} 
            icon={<IconBellRinging size={22}/>} 
            color="orange" 
            desc="Pengiriman menunggu"
            action={() => navigate('/sekolah/konfirmasi')}
        />
        
        {/* Card Total Diterima */}
        <StatCard 
            title="Total Porsi Diterima" 
            value={stats.total_paket_diterima} 
            icon={<IconPackage size={22}/>} 
            color="blue" 
            desc="Akumulasi penerimaan"
        />

        {/* Card Jadwal Berikutnya */}
        <StatCard 
            title="Jadwal Berikutnya" 
            value={stats.jadwal_next ? new Date(stats.jadwal_next).toLocaleDateString('id-ID', {weekday:'long', day:'numeric', month:'short'}) : "-"} 
            icon={<IconCalendarEvent size={22}/>} 
            color="teal" 
            desc={stats.jadwal_next ? "Segera Siapkan Tim" : "Belum ada jadwal"}
        />
      </SimpleGrid>

      <Grid>
        {/* 2. CHART RIWAYAT PENERIMAAN */}
        <Grid.Col span={{ base: 12, md: 8 }}>
            <Card withBorder shadow="sm" radius="md">
                <Title order={4} mb="md">Riwayat Penerimaan (7 Hari)</Title>
                <div style={{ height: 300, width: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chart}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis 
                                dataKey="date" 
                                tickFormatter={(val) => new Date(val).toLocaleDateString('id-ID', {day:'numeric', month:'short'})}
                            />
                            <YAxis />
                            <RechartsTooltip 
                                labelFormatter={(label) => new Date(label).toLocaleDateString('id-ID', {weekday:'long', day:'numeric', month:'long'})}
                            />
                            <Bar dataKey="total_porsi" name="Jumlah Porsi" fill="#40c057" radius={[4, 4, 0, 0]} barSize={50} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </Grid.Col>

        {/* 3. TIMELINE AKTIVITAS TERBARU (Samping Kanan) */}
        <Grid.Col span={{ base: 12, md: 4 }}>
             <Card withBorder shadow="sm" radius="md" h="100%">
                <Title order={4} mb="md">Aktivitas Terbaru</Title>
                
                {/* Tampilan Mobile/Tablet friendly list */}
                <ScrollArea h={300}>
                    {recent.length > 0 ? (
                        <Timeline active={1} bulletSize={24} lineWidth={2}>
                            {recent.map((item, index) => (
                                <Timeline.Item 
                                    key={item.id} 
                                    bullet={item.status_terima === 'Selesai' ? <IconCheck size={12}/> : <IconTruckDelivery size={12}/>}
                                    color={item.status_terima === 'Selesai' ? 'green' : 'blue'}
                                    title={item.menu}
                                >
                                    <Text c="dimmed" size="xs">{item.penyedia}</Text>
                                    <Text size="xs" mt={4}>{new Date(item.tanggal).toLocaleDateString('id-ID', {day:'numeric', month:'short'})}</Text>
                                    <Badge size="xs" mt={4} variant="light" color="gray">{item.jumlah} Porsi</Badge>
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    ) : (
                        <Center h="100%" c="dimmed">Belum ada aktivitas</Center>
                    )}
                </ScrollArea>
            </Card>
        </Grid.Col>
      </Grid>
      
      {/* 4. TABEL DETAIL DI BAWAH */}
      <Paper withBorder shadow="sm" radius="md" mt="lg" p="md">
         <Title order={4} mb="md">Detail Pengiriman Terakhir</Title>
         <ScrollArea>
            <Table verticalSpacing="sm">
                <Table.Thead bg="gray.0">
                    <Table.Tr>
                        <Table.Th>Tanggal</Table.Th>
                        <Table.Th>Detail Menu</Table.Th>
                        <Table.Th>Jumlah</Table.Th>
                        <Table.Th>Status</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length > 0 ? rows : <Table.Tr><Table.Td colSpan={4} align="center">Belum ada data</Table.Td></Table.Tr>}
                </Table.Tbody>
            </Table>
         </ScrollArea>
      </Paper>
    </div>
  );
}